import { custom } from 'devextreme/ui/dialog';
import { MESSAGES } from './Messages';

/**
 * Client half of the generic optimistic-concurrency framework.
 *
 * The API rejects a stale save with HTTP 409 and a body carrying
 * `{ statusCode: 409, code: 'CONCURRENCY_CONFLICT', message }`. This module recognises that response
 * wherever it surfaces and raises a single blocking dialog offering one recovery action: reload the
 * page (the default, for form saves) or refresh just the affected grid (for DevExtreme grids, which
 * pass an action via `createConcurrencyAwareStore`'s `onConflict`).
 *
 * `handleConcurrencyConflict` is the only entry point — detection and the dialog are one call.
 *
 * Errors reach us in three different shapes, so every helper here accepts all of them:
 *   1. an axios error       - form pages (`err.response.status` / `err.response.data`)
 *   2. an XMLHttpRequest    - DevExtreme grids, via `createStore`'s `onAjaxError`
 *   3. a plain string       - what `devextreme-aspnet-data-nojquery` rejects with
 */

// Module-local: nothing outside this file needs it, and every consumer goes through
// isConcurrencyConflict / handleConcurrencyConflict rather than testing the status itself.
const HTTP_CONFLICT = 409;

/** Mirrors ConcurrencyPolicy.ConflictCode on the server. */
export const CONCURRENCY_CONFLICT_CODE = 'CONCURRENCY_CONFLICT';

/**
 * Set on a rejected promise after the dialog has been raised, so a page's own `catch` can skip
 * its red toast instead of stacking a second message on top of the dialog.
 */
export const CONCURRENCY_HANDLED = '__concurrencyHandled';

/**
 * Latched once the page has been committed to a reload. Never cleared — the page is being discarded,
 * and `window.location.reload()` is not instantaneous, so without this a handler queued during
 * teardown reopens the dialog.
 */
let reloadLatched = false;

/** Cleared again for the refresh path, which leaves the page alive and must stay re-armable. */
let dialogOpen = false;

/** Named for readability at the call site below. Tests stub `window.location.reload` to observe it. */
function reloadPage() {
    window.location.reload();
}

/** Test-only reset for the module's dialog latches. */
export function resetConcurrencyStateForTests() {
    reloadLatched = false;
    dialogOpen = false;
}

function parseBody(body) {
    if (!body) return null;
    if (typeof body === 'object') return body;

    if (typeof body === 'string') {
        try {
            const parsed = JSON.parse(body);
            return typeof parsed === 'object' ? parsed : null;
        } catch (e) {
            return null;
        }
    }

    return null;
}

/**
 * Normalises anything the API layer can hand back into `{ status, body, text }`.
 */
function normalize(errLike) {
    if (!errLike) return { status: 0, body: null, text: '' };

    // Plain string rejection (devextreme-aspnet-data-nojquery). It may be a bare message or a
    // JSON body that DevExtreme has already stringified.
    if (typeof errLike === 'string') {
        return { status: 0, body: parseBody(errLike), text: errLike };
    }

    // Raw XMLHttpRequest, or the { xhr, error } object passed to onAjaxError.
    const xhr = errLike.xhr || (typeof errLike.status === 'number' && 'responseText' in errLike ? errLike : null);
    if (xhr) {
        const body = xhr.responseJSON || parseBody(xhr.responseText);
        return { status: xhr.status || 0, body, text: xhr.responseText || '' };
    }

    // Axios error.
    if (errLike.response) {
        const body = parseBody(errLike.response.data);
        return {
            status: errLike.response.status || 0,
            body,
            text: typeof errLike.response.data === 'string' ? errLike.response.data : ''
        };
    }

    // An already-unwrapped response body, handed to us directly by a caller that read a failure out of an
    // HTTP 200. That shape is a legacy workaround, not the pattern to copy — a mutation that did not happen
    // should answer a real status (Architecture.md, "Error contract: evaluation vs mutation"). The WM
    // Transaction endpoints have been converted; this branch stays because other modules have not.
    if (typeof errLike === 'object' && ('statusCode' in errLike || 'code' in errLike || 'message' in errLike)) {
        return { status: 0, body: errLike, text: '' };
    }

    return { status: 0, body: null, text: errLike.message || '' };
}

/**
 * True when the failure is an optimistic-concurrency conflict.
 *
 * Both the HTTP status and the body marker are checked: controllers that have not yet been moved
 * onto `BaseController.MapServiceFailure` still collapse the response to 400 while carrying
 * `statusCode: 409` in the body.
 */
export function isConcurrencyConflict(errLike) {
    const { status, body } = normalize(errLike);

    if (status === HTTP_CONFLICT) return true;
    if (!body) return false;

    return body.code === CONCURRENCY_CONFLICT_CODE || body.statusCode === HTTP_CONFLICT;
}

/**
 * Single implementation of the message-extraction ladder that pages have been copy-pasting.
 */
export function getApiErrorMessage(errLike, fallback) {
    const { body, text } = normalize(errLike);

    return (
        (body && (body.message || body.Message)) ||
        (text && text.trim() ? text : null) ||
        (errLike && errLike.message) ||
        fallback ||
        MESSAGES.ERROR
    );
}

/**
 * Blocking dialog telling the user their save was rejected, with a single action to recover.
 *
 * Two modes:
 *
 *  - **Reload** (default, no `action`) — discards the page. Used for form saves, where the record the
 *    user was editing *is* the page. The OIDC user lives in `InMemoryWebStorage` (see index.js), so
 *    the token does not survive the reload and the app re-runs the authorization-code redirect; that
 *    is silent against a live IdP session and `signinRedirect({ data: { url } })` returns the user to
 *    the same URL, so the visible effect is one extra redirect bounce.
 *
 *  - **Refresh** (`action` supplied) — used for DevExtreme grids, where the conflicting record is one
 *    row and the caller can re-pull just that grid. Keeps the rest of the screen, including anything
 *    the user has typed but not saved.
 *
 * A grid's action must refresh whatever the *changed record derives* (the receipt screens' totals are
 * computed from the detail rows) but must never refetch state the **user owns** — reloading the header
 * would silently discard unsaved header edits, and a detail conflict says nothing about the header
 * anyway, since `UpdateDetail` never writes the header row.
 *
 * @param {*} errLike        the failure, in any of the three shapes this module accepts.
 * @param {object} [action]  `{ label, onConfirm }`. `onConfirm` may return a promise.
 */
function showStaleRecordDialog(errLike, action) {
    // Guarded so several failures arriving together (a multi-row grid save, say) raise one dialog
    // rather than a stack. A plain "visible" flag cleared on dismiss was not enough on the reload
    // path — a second dialog was observed in practice, because clearing it left a window between the
    // button resolving and the browser tearing the page down. Hence the split: the reload path
    // latches permanently, the refresh path leaves the page alive and so must re-arm.
    if (reloadLatched || dialogOpen) return;
    dialogOpen = true;

    const isRefresh = typeof action?.onConfirm === 'function';
    if (!isRefresh) reloadLatched = true;

    const detail = getApiErrorMessage(errLike, MESSAGES.RECORD_CHANGED_BY_ANOTHER_USER);
    const hint = isRefresh ? MESSAGES.RECORD_CHANGED_REFRESH_HINT : MESSAGES.RECORD_CHANGED_RELOAD_HINT;

    custom({
        title: MESSAGES.RECORD_CHANGED_TITLE,
        messageHtml: `<div>${detail} ${hint}</div>`,
        // type + stylingMode + accordianButton is the app's standard primary dialog action.
        // DevExtreme defaults dialog buttons to stylingMode "text", which renders unfilled.
        buttons: [{
            text: isRefresh ? (action.label || MESSAGES.REFRESH) : MESSAGES.RELOAD,
            type: 'default',
            stylingMode: 'contained',
            elementAttr: { class: 'accordianButton' },
            onClick: () => true
        }]
    })
        .show()
        .then(() => {
            if (!isRefresh) {
                // reloadLatched stays set deliberately — see the note above.
                reloadPage();
                return undefined;
            }

            // Re-arm even if the caller's refresh throws. Leaving dialogOpen stuck would silence
            // every later conflict on this page with no error of any kind.
            return Promise.resolve()
                .then(action.onConfirm)
                .catch(() => { /* the caller's refresh failed; nothing useful to add here */ })
                .then(() => { dialogOpen = false; });
        });
}

/**
 * Detect-and-handle in one call. Returns true when the failure was a conflict and the dialog has
 * been raised, so the caller should not also show its own error toast.
 *
 * @param {object} [action] optional `{ label, onConfirm }` — see `showStaleRecordDialog`. Omit for
 *   the default reload behaviour.
 */
export function handleConcurrencyConflict(errLike, action) {
    if (!isConcurrencyConflict(errLike)) return false;

    showStaleRecordDialog(errLike, action);
    return true;
}
